import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import IncomingIconSvg from '../../../assets/icons/icon-tips-statement-incoming.svg?react';
import WithdrawalIconSvg from '../../../assets/icons/icon-tips-statement-withdrawal.svg?react';
import { TipsStatementEntry } from '../model';

type TipsStatementEntryRowProps = {
  entry: TipsStatementEntry;
};

const EntryRow = styled.article.attrs({
  'data-ui': 'tips-statement-entry-row',
})`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.scale.px(12)};
  padding: ${({ theme }) => theme.scale.px(12)} 0;
  border-bottom: ${({ theme }) => theme.scale.px(1)} solid
    ${({ theme }) => theme.colors.border.emphasis};
`;

const EntryMain = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.scale.px(10)};
`;

const EntryDetails = styled.div`
  min-width: 0;
  display: grid;
  gap: ${({ theme }) => theme.scale.px(2)};
`;

const EntryTitle = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(16)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(19)};
`;

const EntryTimestamp = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(12)};
  line-height: ${({ theme }) => theme.scale.px(14)};
  color: ${({ theme }) => theme.colors.text.subdued};
`;

const EntryAmount = styled.p<{ $kind: TipsStatementEntry['kind'] }>`
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(14)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(17)};
  color: ${({ $kind, theme }) =>
    $kind === 'withdrawal'
      ? theme.colors.support.danger
      : theme.colors.support.success};
  opacity: ${({ $kind }) => ($kind === 'withdrawal' ? 0.7 : 1)};
`;

const IncomingIcon = styled(IncomingIconSvg).attrs({ 'aria-hidden': true })`
  width: ${({ theme }) => theme.scale.px(27)};
  height: ${({ theme }) => theme.scale.px(27)};
  flex: 0 0 auto;
  transform: scale(-1, -1);
  transform-origin: center;
  --fill-0: ${({ theme }) => theme.colors.support.infoSubtle};
  --stroke-0: ${({ theme }) => theme.colors.text.primary};
`;

const WithdrawalIcon = styled(WithdrawalIconSvg).attrs({ 'aria-hidden': true })`
  width: ${({ theme }) => theme.scale.px(27)};
  height: ${({ theme }) => theme.scale.px(27)};
  flex: 0 0 auto;
  transform: scale(-1, -1);
  transform-origin: center;
  --fill-0: ${({ theme }) => theme.colors.support.infoSubtle};
  --stroke-0: ${({ theme }) => theme.colors.text.primary};
`;

export function TipsStatementEntryRow({ entry }: TipsStatementEntryRowProps) {
  const { t } = useTranslation();

  return (
    <EntryRow>
      <EntryMain>
        {entry.kind === 'withdrawal' ? <WithdrawalIcon /> : <IncomingIcon />}
        <EntryDetails>
          <EntryTitle>{t(entry.titleKey)}</EntryTitle>
          <EntryTimestamp>{entry.timestamp}</EntryTimestamp>
        </EntryDetails>
      </EntryMain>
      <EntryAmount $kind={entry.kind}>{entry.amount}</EntryAmount>
    </EntryRow>
  );
}
